import React, { useContext, useMemo, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { BudgetExpenseOrIncome } from 'business/constants';
import {
    trackEvent,
    trackEventBudget,
    TRACKABLE_EVENTS,
} from 'helpers/trackerHelpers';
import { useTranslation } from 'react-i18next';
import Typography from '@mui/material/Typography';
import { styled } from '@mui/material/styles';
import { Box } from '@mui/material';
import {
    getExtendedImputationsQuery,
    IMPUTATION_METRICS,
} from 'routes/BudgetModule/config/queries';
import {
    getMetricsByBudgetPhase,
    getBarChartImputation,
} from 'routes/BudgetModule/helpers/query';
import { BUDGET_LINE_ID } from 'routes/BudgetModule/config/constants';
import { UserScopesT, ScopeT, ChartSettingsT } from 'business/definitions';

import { BudgetProposalT } from 'routes/BudgetModule/config/types';
import AddToArgumentModal from 'routes/BudgetModule/components/BudgetProposal/Resource/Analyse/AddToArgumentModal';
import {
    SIFAC_EXCLUDED_DIMENSIONS,
    getImputationDimensionsLabelsAndCodes,
} from 'routes/BudgetModule/config/hooks';
import { getResourceType } from 'routes/BudgetModule/helpers/budgetProposalHelpers';
import { useGetProjects } from 'routes/BudgetModule/shared/useGetProjects';
import AnalyseCharts from './components/AnalyseCharts';
import { useGetBudgetProposalSettings } from '../ExpenseIncome/hooks/budgetProposalSettings';
import { useGetAnalyseFilters } from './hooks/globalFilters';
import { isCustomBudgetProposal } from '../hooks/budgetProposal';
import BudgetFilters from 'routes/BudgetModule/components/shared/Filters/BudgetFilters';
import ErrorComponent from 'components/ErrorBoundary/ErrorComponent';
import { useAddToArgument } from './hooks/addToArgumentHooks';
import ProjectsChart from './components/ProjectsChart';
import { IntegrationContext } from 'routes/BudgetModule/components/BudgetWithProvider/IntegrationContext';
import { ImputationDimensionsContext } from 'routes/BudgetModule/components/BudgetWithProvider/ImputationDimensionsContext';

const BUDGET_EXPENSE_AND_INCOME = [
    BudgetExpenseOrIncome.EXPENSE,
    BudgetExpenseOrIncome.INCOME,
];

type Props = {
    budgetProposal: BudgetProposalT;
    userScopes: UserScopesT;
    resourceName: string;
    resourceSoftwareId: string;
    scopes: ScopeT[];
};

const StyAnalyse = styled(Box, {
    name: 'StyAnalyse',
})({
    display: 'flex',
    alignItems: 'center',
    flexFlow: 'column wrap',
    justifyContent: 'flex-start',
    width: '100%',
});

export type SelectedChartForArgumentT = {
    query: QueryT;
    title: string;
    settings: ChartSettingsT;
    use_get_data?: boolean;
};

const Analyse = ({
    budgetProposal,
    resourceName,
    resourceSoftwareId,
    userScopes,
    scopes,
}: Props): React.ReactElement => {
    const { t } = useTranslation();
    const integrations = useContext(IntegrationContext);
    const isSchool = isCustomBudgetProposal(integrations);

    const { exercice, phase } = budgetProposal;
    const extendedMetricsByTypeAndPhase = useMemo(
        () => getMetricsByBudgetPhase(IMPUTATION_METRICS, phase),
        [phase],
    );

    useGetProjects();

    const imputationRawDimensions = useContext(ImputationDimensionsContext);

    const excludedDimensions = isSchool ? SIFAC_EXCLUDED_DIMENSIONS : [];
    const imputationDimensions = getImputationDimensionsLabelsAndCodes(
        imputationRawDimensions,
        excludedDimensions,
    );

    const pivotTableQuery = getExtendedImputationsQuery(
        [...imputationDimensions, BUDGET_LINE_ID],
        extendedMetricsByTypeAndPhase,
    );
    const { filters, updateFilters, areFetchingFilters } = useGetAnalyseFilters(
        {
            budgetProposal,
            resourceSoftwareId,
        },
    );

    const trackerMetadata = {
        budgetProposal: budgetProposal.name,
        phase: t(`budgetProposal.${budgetProposal.phase}`),
        service: resourceName,
    };

    // tracking on amplitude when user opens the analysis tab
    useEffect(() => {
        trackEvent('Analyse tab opened'); // TODO: remove once CS transitioned to using new trackers
        trackEventBudget(
            TRACKABLE_EVENTS.budget.budgetProposal.analyse.pageOpened,
            trackerMetadata,
        );
    }, []);

    const { budgetProposalId = '' } = useParams();

    const resourceType = getResourceType(budgetProposal.by);

    const {
        settings: budgetProposalSettings,
        isSettingsLoading,
        isError: isSettingsError,
    } = useGetBudgetProposalSettings(
        budgetProposal,
        resourceType,
        resourceSoftwareId,
    );

    const finalPivotTableQueryByExpenseOrIncome =
        BUDGET_EXPENSE_AND_INCOME.reduce(
            (acc: Record<BudgetExpenseOrIncome, QueryT>, expOrInc) => {
                acc[expOrInc] = getBarChartImputation(
                    pivotTableQuery,
                    budgetProposal,
                    resourceSoftwareId,
                    expOrInc,
                    userScopes,
                    scopes,
                    filters,
                );
                return acc;
            },
            { EXPENSE: {}, INCOME: {} } as Record<
                BudgetExpenseOrIncome,
                QueryT
            >,
        );

    const {
        selectedChartForArgument,
        budgetArguments,
        onAddToArgumentModalOpen,
        resetSelectedChart,
        saveChartToArgument,
    } = useAddToArgument(budgetProposalId, resourceSoftwareId, trackerMetadata);

    const isLoading = areFetchingFilters || isSettingsLoading;
    const content = (
        <>
            {!!selectedChartForArgument && (
                <AddToArgumentModal
                    show={!!selectedChartForArgument}
                    closeModal={resetSelectedChart}
                    argumentaires={budgetArguments}
                    addChartToArgument={(arg) =>
                        saveChartToArgument(arg, selectedChartForArgument)
                    }
                    budgetProposalId={budgetProposalId}
                    resourceSoftwareId={resourceSoftwareId}
                />
            )}

            {BUDGET_EXPENSE_AND_INCOME.map((expOrInc) => {
                const settings =
                    expOrInc === BudgetExpenseOrIncome.EXPENSE
                        ? budgetProposalSettings?.analyseExpenseGrid
                        : budgetProposalSettings?.analyseIncomeGrid;

                return (
                    <AnalyseCharts
                        key={String(expOrInc)}
                        expenseOrIncome={expOrInc}
                        pivotTableQuery={pivotTableQuery}
                        query={finalPivotTableQueryByExpenseOrIncome[expOrInc]}
                        exercice={exercice}
                        resourceName={resourceName}
                        onAddToArgumentModalOpen={onAddToArgumentModalOpen}
                        budgetProposal={budgetProposal}
                        resourceType={resourceType}
                        softwareId={resourceSoftwareId}
                        pivotTableSettings={settings?.columns}
                        isLoading={isLoading}
                    />
                );
            })}
            <StyAnalyse>
                <Typography
                    sx={{ textAlign: 'center', margin: '1rem' }}
                    variant="h4"
                    color="primary"
                >
                    {t('budgetProposal.projectsTitle')}
                </Typography>
                <ProjectsChart
                    budgetProposal={budgetProposal}
                    resourceSoftwareId={resourceSoftwareId}
                    extraFilters={filters}
                    settings={budgetProposalSettings?.projectsGrid.columns}
                    isLoading={isLoading}
                    onAddToArgumentModalOpen={onAddToArgumentModalOpen}
                />
            </StyAnalyse>
        </>
    );
    if (isSettingsError) {
        return <ErrorComponent />;
    }
    return (
        <div className="content-wrapper">
            <BudgetFilters
                filters={filters}
                updateFilter={updateFilters}
                budgetBy={budgetProposal.by}
            />
            {content}
        </div>
    );
};

export default Analyse;
